import CheckIcon from '@mui/icons-material/Check';
import { Box, Grid, InputLabel, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import FormSelect from '../../../../shared/FormSelect';
import { SetMenu, SetMenuItem } from '../../../../types/set-menu';
import { SetMenuCourse } from '../../../../enums/set-menu';
import { formatCurrency } from '../../../../../shared/utils';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';

const COURSE_ORDER: { value: SetMenuCourse; label: string }[] = [
    { value: SetMenuCourse.Starter, label: 'Starters' },
    { value: SetMenuCourse.Main, label: 'Mains' },
    { value: SetMenuCourse.Side, label: 'Sides' },
    { value: SetMenuCourse.Dessert, label: 'Desserts' },
    { value: SetMenuCourse.Drink, label: 'Drinks' },
];

const ReservationSetMenu = ({
    name,
    setMenus,
    numberOfGuests: numberOfGuestsFromParent,
}: {
    name: string;
    setMenus: SetMenu[];
    numberOfGuests?: number;
}) => {
    const methods = useFormContext();
    const { control, watch, setValue, trigger } = methods;
    const guestsCount = useWatch({ control, name: 'bookingInfo.adults' });
    const childrenCount = useWatch({ control, name: 'bookingInfo.children' });
    const overnightGuestsCount = useWatch({
        control,
        name: 'bookingInfo.overnightGuests',
    });
    const numberOfGuests = Math.max(
        guestsCount + childrenCount,
        overnightGuestsCount,
    );

    const setMenuOptions = (setMenus || []).map((setMenu) => ({
        value: setMenu.id,
        label: setMenu.name,
    }));

    useEffect(() => {
        if (setMenus && setMenus.length > 0 && !watch(`${name}.templateId`)) {
            const defaultTemplate = setMenus[0];
            if (defaultTemplate) {
                setValue(`${name}.templateId`, defaultTemplate.id);
                setValue(`${name}.quantity`, 1);
            }
        }
    }, [setMenus, name, setValue, watch, trigger]);

    const selectedSetMenu = setMenus?.find(
        (setMenu) => setMenu.id === watch(`${name}.templateId`),
    );

    const isSelected =
        selectedSetMenu?.id != null &&
        watch(`${name}.setMenu.id`) === selectedSetMenu.id;

    const handleSelectSetMenu = () => {
        if (selectedSetMenu?.id != null) {
            setValue(`${name}.setMenu`, {
                id: selectedSetMenu.id,
                name: selectedSetMenu.name,
            });
            setValue(`${name}.pricePerPerson`, selectedSetMenu.pricePerPerson);
            setValue(
                `${name}.quantity`,
                numberOfGuests || numberOfGuestsFromParent || 1,
            );
            trigger(`${name}.guestNotes`);
            trigger(`${name}.primaryActionField`);
        }
    };

    const courseGroups = COURSE_ORDER.map((course) => ({
        ...course,
        items: (selectedSetMenu?.items ?? [])
            .filter((item) => item.course === course.value)
            .sort((a, b) => a.order - b.order),
    })).filter((group) => group.items.length > 0);

    return (
        <Grid
            container
            sx={{
                bgcolor: 'custom.cardActionBackground',
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'custom.grey',
            }}
        >
            <Grid item container xs={12} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <InputLabel
                        htmlFor={`${name}-template-input`}
                        id={`${name}-template-label`}
                    >
                        Price List
                    </InputLabel>
                </Grid>
                <Grid item xs={12}>
                    <FormSelect
                        id={`${name}-template-input`}
                        labelId={`${name}-template-label`}
                        name={`${name}.templateId`}
                        control={control}
                        options={setMenuOptions}
                    />
                </Grid>
            </Grid>
            {selectedSetMenu && (
                <Grid
                    item
                    container
                    xs={12}
                    sx={{
                        mb: 2,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                        }}
                    >
                        <Typography sx={{ fontWeight: 600 }}>
                            Price per person
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                            {formatCurrency(selectedSetMenu.pricePerPerson)}
                        </Typography>
                    </Box>
                    <CustomButton
                        onClick={handleSelectSetMenu}
                        variant={isSelected ? 'outlined' : 'contained'}
                        color='primary'
                        disabled={isSelected}
                        startIcon={isSelected ? <CheckIcon /> : undefined}
                    >
                        {isSelected ? 'Selected' : 'Select'}
                    </CustomButton>
                </Grid>
            )}
            <Box
                sx={{
                    maxHeight: 500,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                }}
            >
                {courseGroups.map((group) => (
                    <Box key={group.value} sx={{ mb: 3 }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontSize: '13px',
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        >
                            {group.label}
                        </Typography>
                        <Grid container spacing={2}>
                            {group.items.map((item: SetMenuItem) => (
                                <Grid item xs={12} key={item.id ?? item.name}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'custom.grey',
                                            borderRadius: 1,
                                            bgcolor: 'primary.contrastText',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                pr: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {item.name}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Box>
        </Grid>
    );
};

export default ReservationSetMenu;
